import React from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';

export default function NotificationDeleter({ onClose }) {
  const { role, notifications = [], deleteNotification } = useAuth();
  const allowed = role === 'Majitel' || role === 'Admin';
  const [busyId, setBusyId] = React.useState(null);
  const [message, setMessage] = React.useState('');
  const [filter, setFilter] = React.useState('');
  const inputRef = React.useRef(null);
  const [confirmItem, setConfirmItem] = React.useState(null);

  React.useEffect(() => {
    // po otevření modalu rovnou fokus do vyhledávacího pole
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  const list = React.useMemo(() => {
    let arr = Array.isArray(notifications) ? notifications.slice() : [];
    // server already prunes to 3 weeks, sort newest first just in case
    arr.sort((a,b) => (Number(b?.createdAt)||0) - (Number(a?.createdAt)||0));
    if (filter && filter.trim().length > 0) {
      const q = filter.trim().toLowerCase();
      arr = arr.filter(n => (n?.popupTitle||'').toLowerCase().includes(q) || (n?.text||'').toLowerCase().includes(q));
    }
    return arr;
  }, [notifications, filter]);

  const onDelete = (item) => {
    if (!allowed) { setMessage('Nemáš oprávnění.'); return; }
    setConfirmItem(item || null);
  };

  const performDelete = async () => {
    if (!allowed) { setMessage('Nemáš oprávnění.'); return; }
    const id = confirmItem?.id;
    if (!id) { setConfirmItem(null); return; }
    setBusyId(id);
    setMessage('');
    try {
      await deleteNotification(id);
      setMessage('Smazáno.');
      setConfirmItem(null);
    } catch (e) {
      setMessage('Chyba: ' + (e?.message || 'unknown'));
    } finally {
      setBusyId(null);
    }
  };

  const close = () => { onClose && onClose(); };

  const content = (
    <Overlay onClick={close}>
      <Modal role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>Smazat notifikaci</Title>
          <Close onClick={close} aria-label="Zavřít">✕</Close>
        </Header>
        {!allowed ? (
          <Info>Pouze Admin/Majitel mohou mazat notifikace.</Info>
        ) : (
          <>
            <Controls>
              <Input
                ref={inputRef}
                placeholder="Hledat podle titulku nebo textu…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                onMouseDown={(e) => { e.stopPropagation(); }}
                onClick={(e) => { e.stopPropagation(); e.currentTarget.focus(); }}
              />
            </Controls>
            <List>
              {list.length === 0 ? (
                <Empty>Žádné notifikace za poslední 3 týdny.</Empty>
              ) : (
                list.map(n => (
                  <Row key={n.id}>
                    {n.popupThumb ? (<Mini src={n.popupThumb} alt="thumb" />) : (<MiniPlaceholder />)}
                    <Col>
                      <strong>{n.popupTitle || 'Oznámení'}</strong>
                      <small>{n.text || ''}</small>
                    </Col>
                    <DelBtn disabled={busyId === n.id} onClick={() => onDelete(n)}>{busyId === n.id ? 'Mažu…' : 'Smazat'}</DelBtn>
                  </Row>
                ))
              )}
            </List>
            {message && <Info style={{marginTop:8}}>{message}</Info>}
          </>
        )}
      </Modal>
      {confirmItem && (
        <ConfirmOverlay onClick={() => setConfirmItem(null)}>
          <ConfirmBox role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <ConfirmTitle>Opravdu smazat notifikaci?</ConfirmTitle>
            <ConfirmText>
              Tato akce odstraní notifikaci pro všechny uživatele.
              <br />
              <strong>{confirmItem.popupTitle || 'Oznámení'}</strong>
            </ConfirmText>
            <ConfirmActions>
              <CancelBtn type="button" onClick={() => setConfirmItem(null)}>Zrušit</CancelBtn>
              <DangerBtn type="button" onClick={performDelete} disabled={busyId === confirmItem.id}>
                {busyId === confirmItem.id ? 'Mažu…' : 'Smazat'}
              </DangerBtn>
            </ConfirmActions>
          </ConfirmBox>
        </ConfirmOverlay>
      )}
    </Overlay>
  );

  const target = typeof document !== 'undefined' ? document.body : null;
  return target ? ReactDOM.createPortal(content, target) : content;
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(10,10,12,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100000;
`;

const Modal = styled.div`
  width: min(900px, 96vw);
  background: linear-gradient(180deg,#0f1720,#11131a);
  border-radius: 12px;
  box-shadow: 0 20px 50px rgba(2,6,23,0.6);
  padding: 18px;
  color: #e6eef8;
  border: 1px solid rgba(255,255,255,0.06);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 700;
`;

const Close = styled.button`
  appearance: none;
  border: 0;
  background: transparent;
  color: #e6eef8;
  font-size: 18px;
  cursor: pointer;
`;

const Controls = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
`;

const Input = styled.input`
  flex: 1;
  height: 40px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.06);
  background: rgba(255,255,255,0.02);
  color: #e6eef8;
  outline: none;
  font-size: 14px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 60vh;
  overflow: auto;
`;

const Empty = styled.div`
  padding: 16px;
  color: #cfdff6;
  text-align: center;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr auto;
  gap: 10px;
  align-items: center;
  padding: 8px;
  border-radius: 8px;
  &:hover { background: rgba(255,255,255,0.04); }
`;

const Mini = styled.img`
  width: 80px;
  height: 45px;
  object-fit: cover;
  border-radius: 6px;
`;

const MiniPlaceholder = styled.div`
  width: 80px;
  height: 45px;
  border-radius: 6px;
  background: rgba(255,255,255,0.06);
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  strong { font-size: 13px; }
  small { font-size: 12px; opacity: 0.9; }
`;

const DelBtn = styled.button`
  appearance: none;
  border: 1px solid rgba(229,57,53,0.5);
  background: rgba(229,57,53,0.15);
  color: #ffb4b4;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
`;

const Info = styled.div`
  color: #cfdff6;
`;

const ConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(10,10,12,0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100001;
`;

const ConfirmBox = styled.div`
  width: min(520px, 92vw);
  background: linear-gradient(180deg,#0f1720,#11131a);
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.06);
  box-shadow: 0 20px 50px rgba(2,6,23,0.6);
  padding: 16px;
  color: #e6eef8;
`;

const ConfirmTitle = styled.div`
  font-weight: 700;
  margin-bottom: 8px;
`;

const ConfirmText = styled.div`
  font-size: 14px;
  opacity: 0.95;
`;

const ConfirmActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 14px;
`;

const CancelBtn = styled.button`
  appearance: none;
  border: 1px solid rgba(255,255,255,0.12);
  background: transparent;
  color: #e6eef8;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
`;

const DangerBtn = styled.button`
  appearance: none;
  border: 0;
  border-radius: 8px;
  background: linear-gradient(90deg,#ef4444,#dc2626);
  color: #fff;
  font-weight: 700;
  padding: 10px 14px;
  cursor: pointer;
  opacity: ${p => (p.disabled ? 0.7 : 1)};
`;
